import {useEffect, useState} from 'react';
import {useAuth} from '../contexts/AuthContext';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    Paper,
    Rating,
    Tooltip,
    Typography
} from '@mui/material';
import {AccessTime, CalendarMonth, CheckCircle, Edit, SportsEsports, Star} from '@mui/icons-material';
import {db} from '../firebase-config';
import {collection, doc, getDoc, getDocs, limit, orderBy, query, where} from 'firebase/firestore';
import UserAvatar from '../components/UserAvatar';
import {differenceInDays, differenceInMonths, differenceInWeeks, format, isToday, isYesterday} from 'date-fns';
import '../styles/Profile.css';

// Hilfsfunktion zur Formatierung des relativen Datums
const formatRelativeDate = (date) => {
  if (!date) return 'Noch nie gespielt';
  
  if (isToday(date)) {
    return `Heute um ${format(date, 'HH:mm')} Uhr`;
  }
  
  if (isYesterday(date)) {
    return `Gestern um ${format(date, 'HH:mm')} Uhr`;
  }
  
  const daysAgo = differenceInDays(new Date(), date);
  if (daysAgo < 7) {
    return `Vor ${daysAgo} Tagen`;
  }
  
  const weeksAgo = differenceInWeeks(new Date(), date);
  if (weeksAgo < 4) {
    return `Vor ${weeksAgo} Wochen`;
  }
  
  const monthsAgo = differenceInMonths(new Date(), date);
  if (monthsAgo < 12) {
    return `Vor ${monthsAgo} Monaten`;
  }
  
  return 'Ewig her';
};

export default function Profile() {
  const { currentUser } = useAuth();
  const { profilePath } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const [userStats, setUserStats] = useState({
    totalGames: 0,
    totalScore: 0,
    averageScore: 0,
    correctAnswersPercent: 0,
    lastPlayed: null,
    registrationDate: null
  });
  const [recentGames, setRecentGames] = useState([]);
  const [ratings, setRatings] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        
        // Finde den Benutzer anhand des Profilpfads
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('profilePath', '==', profilePath));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          setError('Benutzer nicht gefunden');
          setLoading(false);
          return;
        }
        
        const userData = querySnapshot.docs[0].data();
        const userId = querySnapshot.docs[0].id;
        
        console.log('Debug Profile Button:', {
          currentUserId: currentUser?.uid,
          profileUserId: userId,
          match: currentUser?.uid === userId
        });
        
        setProfileUser({
          uid: userId,
          displayName: userData.displayName || 'Anonym',
          photoURL: userData.photoURL || null,
          hasRpmAvatar: userData.hasRpmAvatar || false,
          rpmModelUrl: userData.rpmModelUrl || null,
          profilePath: userData.profilePath,
          createdAt: userData.createdAt?.toDate() || null
        });
        
        // Lade abgeschlossene Spiele des Benutzers
        console.log('Starte Spieleabfrage für User:', userId);
        
        // Neue Abfrage über die player_game_history Sammlung
        const playerGamesQuery = query(
          collection(db, 'player_game_history'),
          where('playerId', '==', userId),
          orderBy('playedAt', 'desc'),
          limit(50)
        );

        // Lade Bewertungen des Benutzers
        const ratingsQuery = query(
          collection(db, 'quiz_feedback'),
          where('userId', '==', userId),
          orderBy('timestamp', 'desc'),
          limit(9)
        );

        const [gamesSnapshot, ratingsSnapshot] = await Promise.all([
          getDocs(playerGamesQuery),
          getDocs(ratingsQuery)
        ]);

        // Verarbeite die Spiele
        const allGames = await Promise.all(gamesSnapshot.docs.map(async doc => {
          const gameData = doc.data();
          
          // Quiz-Titel aus dem gespeicherten Wert oder aus der Quiz-Sammlung
          let quizTitle = gameData.quizTitle || 'Unbekanntes Quiz';
          if (gameData.quizId && !gameData.quizTitle) {
            try {
              const quizDoc = await getDoc(doc(db, 'quizzes', gameData.quizId));
              if (quizDoc.exists()) {
                quizTitle = quizDoc.data().title;
              }
            } catch (error) {
              console.error('Fehler beim Laden des Quiz-Titels:', error);
            }
          }

          return {
            id: gameData.gameSessionId,
            sessionId: gameData.gameSessionId,
            playedAt: gameData.playedAt?.toDate() || null,
            quizTitle: quizTitle,
            score: gameData.score || 0,
            correctAnswers: gameData.correctAnswers || 0,
            totalQuestions: gameData.totalQuestions || 0,
            date: gameData.playedAt?.toDate() || null,
            isGameMaster: gameData.isGameMaster || false,
            position: gameData.position || 0,
            totalPlayers: gameData.totalPlayers || 1
          };
        }));

        // Gruppiere Spiele nach Session-ID und behalte nur den neuesten Eintrag
        const groupedGames = allGames.reduce((acc, current) => {
          const existing = acc[current.sessionId];
          if (!existing || (existing.playedAt && current.playedAt && current.playedAt > existing.playedAt)) {
            acc[current.sessionId] = current;
          }
          return acc;
        }, {});

        // Konvertiere zurück zu Array und sortiere nach Datum
        const sortedGames = Object.values(groupedGames)
          .sort((a, b) => {
            if (!a.date || !b.date) return 0;
            return b.date.getTime() - a.date.getTime();
          })
          .slice(0, 6);

        // Berechne den Durchschnitt der korrekten Antworten aus allen Spielen
        const totalCorrectAnswers = sortedGames.reduce((sum, game) => sum + game.correctAnswers, 0);
        const totalQuestions = sortedGames.reduce((sum, game) => sum + game.totalQuestions, 0);
        const averageCorrectAnswers = totalQuestions > 0 
          ? Math.round((totalCorrectAnswers / totalQuestions) * 100)
          : 0;

        // Verarbeite Bewertungen
        const userRatings = await Promise.all(
          ratingsSnapshot.docs.map(async docSnapshot => {
            const ratingData = docSnapshot.data();

            try {
              const quizRef = doc(db, 'quizzes', ratingData.quizId);
              const quizDoc = await getDoc(quizRef);
              const quizData = quizDoc.exists() ? quizDoc.data() : null;
              return {
                id: docSnapshot.id,
                quizTitle: quizData?.title || 'Gelöschtes Quiz',
                rating: ratingData.rating,
                feedback: ratingData.feedback,
                date: ratingData.timestamp?.toDate() || null
              };
            } catch (error) {
              console.error('Fehler beim Laden des Quiz:', error);
              return null;
            }
          })
        );

        // Filtere ungültige Bewertungen und sortiere nach Datum
        const filteredRatings = userRatings
          .filter(rating => rating !== null)
          .sort((a, b) => (b.date || 0) - (a.date || 0))
          .slice(0, 6);

        // Verwende die Statistiken direkt aus dem Benutzerdokument
        setUserStats({
          totalGames: userData.totalGamesPlayed || 0,
          totalScore: userData.totalScore || 0,
          averageScore: userData.totalGamesPlayed > 0 
            ? Math.round(userData.totalScore / userData.totalGamesPlayed) 
            : 0,
          correctAnswersPercent: averageCorrectAnswers,
          lastPlayed: userData.lastPlayed?.toDate() || null,
          registrationDate: userData.createdAt?.toDate() || null
        });

        setRecentGames(sortedGames);
        setRatings(filteredRatings);
        setLoading(false);
      } catch (error) {
        console.error('Fehler beim Laden der Benutzerstatistiken:', error);
        setError('Fehler beim Laden der Statistiken');
        setLoading(false);
      }
    };

    if (!currentUser) {
      return;
    }

    if (profilePath) {
      loadUserData();
    } else {
      setError('Benutzer nicht gefunden');
      setLoading(false);
    }
  }, [profilePath, currentUser]);

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container className="profile-container">
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : profileUser ? (
        <Grid container spacing={3}>
          {/* Linke Spalte */}
          <Grid item xs={12} md={3}>
            {/* Profilbox */}
            <Paper className="profile-paper profile-box">
              <Box className="profile-avatar-container">
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                  <UserAvatar
                    src={profileUser?.photoURL}
                    alt={profileUser?.displayName}
                    size={180}
                    hasRpmAvatar={profileUser?.hasRpmAvatar}
                    rpmModelUrl={profileUser?.rpmModelUrl}
                    fallbackText={profileUser?.displayName?.charAt(0).toUpperCase()}
                    sx={{ fontSize: '4rem' }}
                  />
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mt: 2,
                      mb: 1,
                      color: 'rgba(255, 255, 255, 0.87)'
                    }}
                  >
                    {profileUser?.displayName}
                  </Typography>
                  {currentUser?.uid === profileUser?.uid && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Edit />}
                      sx={{ 
                        mb: 2,
                        borderColor: 'rgba(255, 255, 255, 0.12)',
                        color: 'rgba(255, 255, 255, 0.87)',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          backgroundColor: 'rgba(255, 255, 255, 0.08)'
                        }
                      }}
                      onClick={() => {
                        console.log('Navigate to edit profile');
                        navigate('/profil/edit');
                      }}
                    >
                      Profil bearbeiten
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Statistikbox */}
            <Paper className="profile-paper profile-box" sx={{ mt: 3 }}>
              <Typography variant="h6" className="profile-title">
                Statistiken
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <SportsEsports sx={{ color: 'var(--color-secondary)', fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.87)' }}>
                        {userStats.totalGames}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                        Gespielte Spiele
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircle sx={{ color: 'var(--color-secondary)', fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.87)' }}>
                        {userStats.correctAnswersPercent}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                        Ø Richtig
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Star sx={{ color: 'var(--color-secondary)', fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.87)' }}>
                        {userStats.averageScore}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                        Ø Punktzahl
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <AccessTime sx={{ color: 'var(--color-secondary)', fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.87)' }}>
                        {formatRelativeDate(userStats.lastPlayed)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                        Letztes Spiel
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CalendarMonth sx={{ color: 'var(--color-secondary)', fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.87)' }}>
                        {userStats.registrationDate ? format(userStats.registrationDate, 'dd.MM.yyyy') : 'Unbekannt'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                        Registriert seit
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Rechte Spalte */}
          <Grid item xs={12} md={9}>
            {/* Letzte Spiele */}
            <Paper className="profile-paper profile-box">
              <Typography variant="h6" className="profile-title">
                <SportsEsports /> Letzte Spiele
              </Typography>
              {recentGames.length > 0 ? (
                <Box className="games-grid">
                  {recentGames.map((game) => (
                    <Box key={game.id} className="game-item">
                      <Box className="game-header">
                        <Typography className="game-title">
                          {game.quizTitle}
                        </Typography>
                        {game.isGameMaster && (
                          <Tooltip 
                            title="Game Master"
                            placement="top"
                            TransitionProps={{ style: { marginBottom: '3px' } }}
                            classes={{
                              tooltip: 'MuiTooltip-tooltip'
                            }}
                          >
                            <IconButton 
                              size="small" 
                              disableRipple 
                              className="icon-button-transparent"
                            >
                              <Star className="game-master-icon" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box className="game-stats">
                        {game.totalPlayers > 1 && (
                          <Box className="game-stat-position">
                            <Box 
                              className={`position-circle ${
                                game.position === 1 ? 'gold' : 
                                game.position === 2 ? 'silver' : 
                                game.position === 3 ? 'bronze' : 
                                'other'
                              }`}
                            >
                              <span>{game.position}</span>
                            </Box>
                          </Box>
                        )}

                        <Box className="game-stat-score">
                          <Box className="score-container">
                            <span className="score-number">{game.score}</span>
                            <span className="score-label">Punkte</span>
                          </Box>
                        </Box>

                        <Box className="game-stat-answers">
                          <Tooltip 
                            title="Korrekte Antworten"
                            placement="top"
                            TransitionProps={{ style: { marginBottom: '3px' } }}
                            classes={{
                              tooltip: 'MuiTooltip-tooltip'
                            }}
                          >
                            <Box className="answers-circle">
                              <CircularProgress
                                variant="determinate"
                                value={Math.round((game.correctAnswers / game.totalQuestions) * 100)}
                                size={56}
                                thickness={3}
                                sx={{
                                  color: '#4caf50',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                  }
                                }}
                              />
                              <CircularProgress
                                variant="determinate"
                                value={100}
                                size={56}
                                thickness={3}
                                sx={{
                                  color: '#ff9800',
                                  opacity: 0.3,
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                }}
                              />
                              <Box className="answers-text">
                                <Typography className="answers-percentage">{Math.round((game.correctAnswers / game.totalQuestions) * 100)}%</Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Typography className="game-date">
                        {formatRelativeDate(game.date)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Noch keine Spiele gespielt
                </Typography>
              )}
            </Paper>

            {/* Bewertungen */}
            <Paper className="profile-paper profile-box" sx={{ mt: 3 }}>
              <Typography variant="h6" className="profile-title">
                <Star /> Letzte Bewertungen
              </Typography>
              {ratings.length > 0 ? (
                <Box className="ratings-grid">
                  {ratings.map((rating) => (
                    <Box key={rating.id} className="rating-item">
                      <Box className="rating-header">
                        <Typography className="rating-title">
                          {rating.quizTitle}
                        </Typography>
                        <Rating value={rating.rating} readOnly size="small" />
                      </Box>
                      {rating.feedback && (
                        <Typography className="rating-content">
                          &quot;{rating.feedback}&quot;
                        </Typography>
                      )}
                      <Typography className="rating-date">
                        {rating.date ? format(rating.date, 'dd.MM.yyyy HH:mm') : 'Datum unbekannt'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Noch keine Bewertungen
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      ) : null}
    </Container>
  );
} 